import { cn } from '@/lib/cn';

export interface OpcaoSegmentada<T extends string> {
  readonly valor: T;
  readonly rotulo: string;
  /** Um detalhe menor ao lado do rótulo — a contagem, o tipo da tipologia. */
  readonly detalhe?: string;
}

/**
 * Um grupo de opções excludentes, uma acesa por vez.
 *
 * É o mesmo gesto em três lugares da aba de itens — qual tipologia está lançando, quais
 * serviços a lista mostra, de que escopo é a soma —, e três desenhos para o mesmo gesto
 * fariam a pessoa reaprender a cada cartão. Um só, e o `aria-pressed` diz o estado sem
 * depender da cor (PLANO_GERAL §4.4).
 *
 * Não é uma faixa de abas: as abas trocam o conteúdo inteiro; isto troca um parâmetro do
 * conteúdo que continua o mesmo.
 */
export function Segmentado<T extends string>({
  label,
  atual,
  opcoes,
  onEscolher,
  className,
}: {
  /** O que o grupo escolhe, dito para quem não vê a tela. */
  readonly label: string;
  readonly atual: T;
  readonly opcoes: readonly OpcaoSegmentada<T>[];
  readonly onEscolher: (valor: T) => void;
  readonly className?: string;
}) {
  return (
    <div
      role="group"
      aria-label={label}
      className={cn(
        'inline-flex max-w-full flex-wrap gap-0.5 rounded-md border border-(--color-border-subtle) bg-(--color-surface-muted) p-0.5',
        className,
      )}
    >
      {opcoes.map((opcao) => {
        const acesa = opcao.valor === atual;
        return (
          <button
            key={opcao.valor}
            type="button"
            aria-pressed={acesa}
            onClick={() => onEscolher(opcao.valor)}
            className={cn(
              // 44px no dedo, 32 no ponteiro — a mesma razão do botão.
              'flex h-11 min-w-0 items-center gap-1.5 rounded px-3 text-sm md:h-8',
              acesa
                ? 'bg-(--color-surface-raised) font-medium text-(--color-content) shadow-sm'
                : 'text-(--color-content-muted) hover:text-(--color-content)',
            )}
          >
            <span className="truncate">{opcao.rotulo}</span>
            {opcao.detalhe !== undefined && (
              <span className="tabular shrink-0 text-xs font-normal text-(--color-content-muted)">
                {opcao.detalhe}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
